import { useEffect } from "react";
import Spinner from "@components/Spinner/Spinner";
import { useEmptyNotification } from "@features/notifications/NotificationToasts/hooks/useEmptyNotification";
import { NotificationComponentFactory } from "@features/notifications/NotificationToasts/utlis/helpers";
import useNotificationStore from "@features/notifications/store/useNotificationStore";
import { Transition, TransitionChild } from "@headlessui/react";

/**
 * Displays notification toasts below the header.
 *
 * This component listens to the notification state from the `useNotificationStore` hook,
 * and renders a notification toast when a notification is present. The toast automatically
 * disappears after 5 seconds or when the user clicks the overlay.
 **/
export default function NotificationToasts(): React.ReactElement {
  const { handleEmptyNotification } = useEmptyNotification();

  const [notification, isNotificationLoading, isNotificationDisplayed] =
    useNotificationStore((store) => [
      store.notification,
      store.isNotificationLoading,
      store.isNotificationDisplayed,
    ]);

  const notificationType = notification.type;
  const text = notification.text;

  useEffect(() => {
    if (!isNotificationDisplayed) return;

    const timer = setTimeout(() => {
      handleEmptyNotification();
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, [isNotificationDisplayed, handleEmptyNotification]);

  const NotificationComponent = NotificationComponentFactory({
    notificationType,
  });

  return (
    <Transition
      as={"div"}
      show={isNotificationDisplayed}
      data-testid="notification-toast-component"
    >
      <TransitionChild
        as={"div"}
        enterFrom="opacity-0 translate-y-full"
        enterTo="opacity-100 translate-y-0"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-full"
      >
        <div
          className="fixed top-0 h-lvh w-lvw bg-transparent"
          onClick={handleEmptyNotification}
        ></div>
      </TransitionChild>

      <TransitionChild
        as={"div"}
        className="absolute w-full overflow-hidden px-3 transition-all duration-1000 md:w-svw md:px-0"
        enterFrom="-translate-y-6 max-h-0"
        enterTo="translate-y-0 max-h-96"
        leaveFrom="translate-y-0 max-h-96"
        leaveTo="-translate-y-2 max-h-0"
      >
        <div className="mx-auto my-6 flex w-full min-w-[100px] items-center justify-center rounded-[60px] bg-gray-850 px-3  text-white shadow-[0px_0px_16px_3px_rgba(0,_0,_0,_0.25)] transition-all duration-700 md:w-max md:px-6">
          {isNotificationLoading ? (
            <div className="px-4 py-2">
              <Spinner
                className="py-0"
                outerClassName="h-6 w-6"
                innerClassName="h-4 w-4 bg-gray-850"
              />
            </div>
          ) : (
            <NotificationComponent
              text={text}
              link={notification.link || ""}
              callback={handleEmptyNotification}
              icon={notification.icon}
            />
          )}
        </div>
      </TransitionChild>
    </Transition>
  );
}
